import { useEffect, useState } from 'react';
import axios from 'axios';
import Accordion from '/components/Accordions/Accordion'; 
import Sidebar from '/components/Layout/Sidebar';
import MissionsUtilityPanel from '/components/Missions/MissionsUtilityPanel';
import infoPaneStyles from '/components/Panes/InfoPane.module.css'; 
import sectionStyles from '/components/Sections/Section.module.css';
import styles from './Missions.module.css';
import { useRouter } from 'next/router';

const Missions = () => {
    const router = useRouter();
    const [drafts, setDrafts] = useState([]);
    const [availableMissions, setAvailableMissions] = useState([]);
    const [missionsUnderReview, setMissionsUnderReview] = useState([]);

    useEffect(() => {
        console.log('Fetching missions from API...');
        if (!router.isReady) return;
        axios.get('/api/missions/missions', {
            params: {
                type: router.query.type || '',
                contentId: router.query.contentId || '',
            },
        })
            .then(response => {
                console.log('API Response:', response.data);
                setDrafts(response.data.drafts || []);
                setAvailableMissions(response.data.availableMissions || []);
                setMissionsUnderReview(response.data.missionsUnderReview || []);
            })
            .catch(error => console.error('Error fetching missions:', error));
    }, [router.isReady, router.query.type, router.query.contentId]);

    /**
     * Handle draft deletion by calling our new DELETE API route.
     * After deleting, remove the draft from state so the UI updates.
     */
    const handleDeleteDraft = async (draftId) => {
        if (!confirm('Are you sure you want to delete this draft?')) return;

        try {
            await axios.delete(`/api/missions/deleteDraft?draftId=${draftId}`);
            // Filter the deleted draft out of our local state
            setDrafts((prevDrafts) => prevDrafts.filter(d => d.draft_id !== draftId));
        } catch (err) {
            console.error('Error deleting draft:', err);
            alert('Failed to delete draft.');
        }
    };

    // Render required fields for a mission
    const renderRequiredFields = (mission) => {
        if (mission.requirements) {
            const labels = { name: 'Name Update', alternativeNames: 'Alternative Names', description: 'Description', difficulty: 'Difficulty', categories: 'Category', contentType: 'Content Type', centerGravity: 'Center of Gravity', weightPlacement: 'Weight Placement', inertia: 'Inertia' };
            const requiredList = Object.entries(labels).filter(([key]) => mission.requirements[key] === 'required').map(([, label]) => label);
            if (mission.requirements.galleryImages?.mode === 'required') requiredList.push(`${mission.requirements.galleryImages.minimum} Gallery Image${mission.requirements.galleryImages.minimum === 1 ? '' : 's'}`);
            Object.entries(mission.requirements.perspectives || {}).forEach(([perspective, count]) => requiredList.push(`${count} ${perspective.replace('_', ' ')} perspective link${count === 1 ? '' : 's'}`));
            return requiredList.length ? <><p><strong>Required Fields:</strong></p><ul>{requiredList.map((field) => <li key={field}>{field}</li>)}</ul></> : null;
        }
        const requiredList = [];

        if (mission.description_required === 1) requiredList.push('Description');
        if (mission.difficulty_required === 1) requiredList.push('Difficulty');
        if (mission.category_required === 1) requiredList.push('Category');
        if (mission.type_required === 1) requiredList.push('Type');
        if (mission.name_update_required === 1) requiredList.push('Name Update');
        if (mission.gallery_image_update_required === 1) requiredList.push('Gallery Image');

        if (requiredList.length === 0) return null;

        return (
            <>
                <p><strong>Required Fields:</strong></p>
                <ul>
                    {requiredList.map((field, idx) => <li key={idx}>{field}</li>)}
                </ul>
            </>
        );
    };

    // Render suggested fields for a mission
    const renderSuggestedFields = (mission) => {
        if (mission.requirements) {
            const labels = { name: 'Name Update', alternativeNames: 'Alternative Names', description: 'Description', difficulty: 'Difficulty', categories: 'Category', contentType: 'Content Type', centerGravity: 'Center of Gravity', weightPlacement: 'Weight Placement', inertia: 'Inertia' };
            const suggestedList = Object.entries(labels).filter(([key]) => mission.requirements[key] === 'suggested').map(([, label]) => label);
            if (mission.requirements.galleryImages?.mode === 'suggested') suggestedList.push('Gallery Images');
            return suggestedList.length ? <><p><strong>Suggested Fields:</strong></p><ul>{suggestedList.map((field) => <li key={field}>{field}</li>)}</ul></> : null;
        }
        const suggestedList = [];

        if (mission.description_suggested === 1) suggestedList.push('Description');
        if (mission.difficulty_suggested === 1) suggestedList.push('Difficulty');
        if (mission.category_suggested === 1) suggestedList.push('Category');
        if (mission.type_suggested === 1) suggestedList.push('Type');
        if (mission.name_update_suggested === 1) suggestedList.push('Name Update');
        if (mission.gallery_image_update_suggested === 1) suggestedList.push('Gallery Image');

        if (suggestedList.length === 0) return null;

        return (
            <>
                <p><strong>Suggested Fields:</strong></p>
                <ul>
                    {suggestedList.map((field, idx) => <li key={idx}>{field}</li>)}
                </ul>
            </>
        );
    };

    return (
        <section id="missions" className={`${sectionStyles.section} ${styles.missionsPage}`}>
          <div className={sectionStyles.pageContainer}>
            <div className={styles.missionsMain}>
            {/* Info Pane Top */}
            <div className={`${infoPaneStyles.infoPane} ${styles.hero}`}>
                <h1>Missions</h1>
                <p>
                    Missions allow users to contribute valuable content to existing content items.
                    Earn points for successful submissions and help improve the database!
                </p>
            </div>

            {/* Main Content with Accordions */}
            <div className={`${sectionStyles.mainContent} ${styles.missionSections}`}>

                {/* Drafts Section */}
                <div className={`${infoPaneStyles.resizableInfoPane} ${styles.missionSection}`}>
                    <h1>User Drafts</h1>
                    {Array.isArray(drafts) && drafts.length > 0 ? (
                        <Accordion
                            items={drafts.map(draft => ({
                                header: `${draft.name || 'Unnamed Mission'} - Draft ID: ${draft.draft_id}`,
                                content: (
                                    <div className={styles.actionRow}>
                                        <a
                                            href={`/MissionTaker?draftId=${draft.draft_id}`}
                                            className={styles.primaryButton}
                                        >
                                            Continue Draft
                                        </a>
                                        {/* Use a button instead of an anchor for delete */}
                                        <button
                                            onClick={() => handleDeleteDraft(draft.draft_id)}
                                            className={styles.deleteButton}
                                        >
                                            Delete Draft
                                        </button>
                                    </div>
                                )
                            }))}
                        />
                    ) : (
                        <p className={styles.emptyState}>No drafts found.</p>
                    )}
                </div>

                {/* Available Missions Section */}
                <div className={`${infoPaneStyles.resizableInfoPane} ${styles.missionSection} ${styles.tabGradientSection}`}>
                    <h1>Available Missions</h1>
                    {Array.isArray(availableMissions) && availableMissions.length > 0 ? (
                        <Accordion
                            items={availableMissions.map(mission => ({
                                header: mission.name,
                                content: (
                                    <div>
                                        <p>{mission.description}</p>
                                        {renderRequiredFields(mission)}
                                        {renderSuggestedFields(mission)}

                                        {mission.public_hash ? (
                                            <a
                                                href={`/MissionTaker?hash=${mission.public_hash}`}
                                                className={styles.primaryButton}
                                            >
                                                Start Mission
                                            </a>
                                        ) : (
                                            <p style={{ color: 'red' }}>
                                                No public hash available for this mission.
                                            </p>
                                        )}
                                    </div>
                                )
                            }))}
                        />
                    ) : (
                        <p className={styles.emptyState}>No active missions found.</p>
                    )}
                </div>

                {/* Missions Under Review Section */}
                <div className={`${infoPaneStyles.resizableInfoPane} ${styles.missionSection} ${styles.tabGradientSection}`}>
                    <h1>Missions Under Review</h1>
                    {Array.isArray(missionsUnderReview) && missionsUnderReview.length > 0 ? (
                        <Accordion
                            items={missionsUnderReview.map(mission => ({
                                header: mission.name,
                                content: (
                                    <div>
                                        <p>{mission.description}</p>
                                        {renderRequiredFields(mission)}
                                        {renderSuggestedFields(mission)}
                                    </div>
                                )
                            }))}
                        />
                    ) : (
                        <p className={styles.emptyState}>No missions under review.</p>
                    )}
                </div>
            </div>
            </div>
            <Sidebar>
              <MissionsUtilityPanel
                drafts={drafts}
                availableCount={availableMissions.length}
                reviewCount={missionsUnderReview.length}
              />
            </Sidebar>
          </div>
        </section>
    );
};

export default Missions;
